/**
 * @file LazyVideo.jsx
 * @description A silent, looping product video that costs nothing until it is needed:
 *   - nothing downloads until the video is near the viewport (only the small poster is fetched);
 *   - then it plays muted, inline, on a loop, and pauses again when scrolled away;
 *   - visitors who ask for reduced motion get the poster and the player controls instead of autoplay.
 *   Space is reserved with the video's aspect ratio, so the page never jumps when it loads.
 * @module components/LazyVideo
 */
import { useEffect, useRef, useState } from "react";
import { useOnScreen } from "../hooks/useOnScreen";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

/**
 * @param {{ sources: Array<{ src: string, type: string }>, poster: string, label: string, width: number, height: number,
 *   style?: object, rootMargin?: string, testId?: string }} props
 */
export default function LazyVideo({ sources, poster, label, width, height, style, rootMargin = "300px", testId }) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const near = useOnScreen(wrapRef, { rootMargin });
  const visible = useOnScreen(wrapRef, { threshold: 0.25 });
  const reduced = usePrefersReducedMotion();
  const [load, setLoad] = useState(false);

  useEffect(() => { if (near) setLoad(true); }, [near]);

  // Sources are added late, so the element must be told to pick them up once.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !load) return;
    v.muted = true; // React does not reliably set the muted attribute, and browsers only autoplay muted video
    if (typeof v.load === "function") v.load();
  }, [load]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !load) return;
    if (visible && !reduced) {
      const p = typeof v.play === "function" ? v.play() : null;
      if (p && typeof p.catch === "function") p.catch(() => {}); // autoplay refused: the poster stays, nothing breaks
    } else if (typeof v.pause === "function") {
      v.pause();
    }
  }, [load, visible, reduced]);

  return (
    <div ref={wrapRef} data-testid={testId} style={{ position: "relative", width: "100%", aspectRatio: `${width} / ${height}`, overflow: "hidden", ...style }}>
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="none"
        poster={poster}
        controls={reduced}
        aria-label={label}
        title={label}
        width={width}
        height={height}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", objectFit: "cover", background: "#12103A" }}
      >
        {load && sources.map((s) => <source key={s.src} src={s.src} type={s.type} />)}
      </video>
    </div>
  );
}
